import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowUp, Sparkles, Loader2, Code2, Eye, Coins, LogIn, Zap, Menu, Download, Github, Rocket, Share2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { SAMPLE_PROMPTS } from '../mock/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BuyCreditsModal from '../components/BuyCreditsModal';
import SandpackBuilder from '../components/SandpackBuilder';
import DeployGitHubModal from '../components/DeployGitHubModal';
import DeployCloudflareModal from '../components/DeployCloudflareModal';
import ShareBuildModal from '../components/ShareBuildModal';
import ProUpsellBanner from '../components/ProUpsellBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getSessionId() {
  let id = localStorage.getItem('tfu_session');
  if (!id) {
    id = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('tfu_session', id);
  }
  return id;
}

function downloadProject(name, files) {
  // Simple zip-less download: bundle all files as a single readable .txt project dump.
  // For users who want the real project, we also expose individual file copy via Sandpack.
  const parts = [`# ${name || 'Transformerus Project'}`, ''];
  Object.entries(files).forEach(([path, code]) => {
    parts.push(`\n=== ${path} ===\n${code}`);
  });
  const blob = new Blob([parts.join('\n')], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(name || 'project').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function Builder() {
  const [params] = useSearchParams();
  const initialPrompt = params.get('prompt') || '';
  const { user, login } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(initialPrompt);
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId());
  const [balance, setBalance] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [buildsToday, setBuildsToday] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectFiles, setProjectFiles] = useState({});
  const [projectEntry, setProjectEntry] = useState('/App.js');
  const [activeView, setActiveView] = useState('preview'); // 'preview' | 'code'
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployCfOpen, setDeployCfOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [missingDep, setMissingDep] = useState(null);
  const scrollRef = useRef(null);
  const sentInitial = useRef(false);

  // Load credit balance for authenticated users
  const refreshBalance = async () => {
    if (!user) {
      setBalance(null);
      setIsPro(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/credits/me`, { withCredentials: true });
      setBalance(res.data?.balance ?? 0);
    } catch (_) {
      setBalance(null);
    }
    try {
      const sub = await axios.get(`${API}/subscription/me`, { withCredentials: true });
      const active = sub.data?.active;
      const tier = sub.data?.subscription?.tier;
      setIsPro(Boolean(active && tier === 'pro'));
    } catch (_) {
      setIsPro(false);
    }
    try {
      const usage = await axios.get(`${API}/build/usage/today`, { withCredentials: true });
      setBuildsToday(usage.data?.builds_today || 0);
    } catch (_) {
      setBuildsToday(0);
    }
    try {
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setIsAdmin(Boolean(meRes.data?.is_admin));
    } catch (_) {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    refreshBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // After returning from the GitHub-deploy connect flow, auto-open the deploy modal
  useEffect(() => {
    if (params.get('github_connected') === '1') {
      setDeployOpen(true);
      // clean the query param so we don't keep re-opening on refresh
      const url = new URL(window.location.href);
      url.searchParams.delete('github_connected');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate any existing project tied to this session id (lets users return to their build).
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/build/${sessionId}`);
        if (res.data?.files && Object.keys(res.data.files).length > 0) {
          setProjectFiles(res.data.files);
          setProjectEntry(res.data.entry || '/App.js');
          setProjectName(res.data.name || '');
        }
      } catch (_) { /* ignore */ }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (initialPrompt && !sentInitial.current) {
      sentInitial.current = true;
      sendMessage(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      const v = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (v) v.scrollTop = v.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: 'user', content }]);
    setInput('');
    setLoading(true);

    // Resilient retry: transient failures (network drop during a Render
    // redeploy, 502/503/504 gateway hiccups, request abort) are silently
    // retried up to 3 attempts with linear backoff. Non-transient errors
    // (auth, bad input, out-of-credits, LLM rejection) fail fast.
    const TRANSIENT_STATUS = new Set([0, 502, 503, 504]);
    const callBuild = async () => {
      return axios.post(
        `${API}/build`,
        {
          session_id: sessionId,
          message: content,
          previous_files: Object.keys(projectFiles).length > 0 ? projectFiles : null,
        },
        { withCredentials: true, timeout: 180000 }
      );
    };

    const MAX_ATTEMPTS = 3;
    let res = null;
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        res = await callBuild();
        break; // success
      } catch (err) {
        lastError = err;
        const status = err?.response?.status ?? 0;
        const isTransient =
          TRANSIENT_STATUS.has(status) ||
          err?.code === 'ECONNABORTED' ||
          err?.code === 'ERR_NETWORK' ||
          err?.message === 'Network Error';
        if (!isTransient || attempt === MAX_ATTEMPTS) {
          break;
        }
        // Surface a soft retry hint to the user so they know we're handling it
        setMessages((m) => {
          const last = m[m.length - 1];
          if (last?.role === 'assistant' && last?.transient) {
            return [
              ...m.slice(0, -1),
              { ...last, content: `Reconnecting… (attempt ${attempt + 1} of ${MAX_ATTEMPTS})` },
            ];
          }
          return [
            ...m,
            {
              role: 'assistant',
              transient: true,
              content: `Reconnecting… (attempt ${attempt + 1} of ${MAX_ATTEMPTS})`,
            },
          ];
        });
        // Linear backoff: 1.5s, 3s
        await new Promise((r) => setTimeout(r, 1500 * attempt));
      }
    }

    try {
      if (!res) throw lastError || new Error('Failed to reach build agent');

      // Strip the transient "Reconnecting…" status message if any
      setMessages((m) => m.filter((x) => !x.transient));

      const data = res.data || {};
      setMissingDep(null);
      setProjectName(data.name || projectName || 'Untitled');
      setProjectEntry(data.entry || '/App.js');
      setProjectFiles(data.files || {});
      const tierLabel = data.model_tier === 'premium' ? ' · Pro (Claude)' : '';
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: (data.explanation || 'Built! Preview is on the right.') + tierLabel },
      ]);
      setActiveView('preview');
      await refreshBalance();
    } catch (e) {
      console.error(e);
      // Strip any transient "Reconnecting…" status message before showing the error
      setMessages((m) => m.filter((x) => !x.transient));
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        const bal = (typeof detail === 'object' && detail?.balance) ?? 0;
        setBalance(bal);
        setMessages((m) => [
          ...m,
          {
            role: 'assistant',
            content: `You're out of credits (balance: ${bal}). Top up to keep building — credits are used for every build call.`,
            outOfCredits: true,
          },
        ]);
        toast.error('Out of credits — top up to continue');
      } else {
        // Sanitize any raw stacktraces / vendor prefixes for human display
        let msg = typeof detail === 'string' ? detail : 'Failed to reach build agent. Please try again.';
        if (msg.length > 240) {
          msg = msg.slice(0, 240).replace(/\s+\S*$/, '') + '…';
        }
        // Strip noisy provider prefixes
        msg = msg
          .replace(/^Code-gen LLM error:\s*/i, '')
          .replace(/^litellm\.\w+:\s*/i, '')
          .replace(/^[A-Za-z]+Exception\s*-\s*/i, '');
        toast.error(msg);
        setMessages((m) => [
          ...m,
          { role: 'assistant', content: `Sorry — ${msg}` },
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const lowBalance = user && balance !== null && balance <= 3;
  const hasProject = Object.keys(projectFiles).length > 0;

  // Debounce repeat firings — Sandpack can emit the same error multiple times
  const handleDependencyError = useCallback((pkgName) => {
    if (!pkgName) return;
    setMissingDep((prev) => (prev === pkgName ? prev : pkgName));
  }, []);

  const regenerateWithoutMissingDep = () => {
    if (!missingDep) return;
    const recoveryPrompt =
      `The previous version of this app imported the package "${missingDep}" which is NOT available in the preview sandbox.\n` +
      `Please regenerate the SAME app, keeping all features and behavior intact, but WITHOUT importing "${missingDep}". ` +
      `If you need similar functionality, implement it from scratch using only the standard allowed libraries (react, react-router-dom, lucide-react, recharts, framer-motion, date-fns, clsx).`;
    setMissingDep(null);
    sendMessage(recoveryPrompt);
  };

  return (
    <div className="flex h-screen bg-neutral-50">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-white border-r border-neutral-200">
        <div className="p-4 border-b border-neutral-200">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Transformerus" className="w-8 h-8 object-contain" />
            <span className="tfu-logo text-[17px] tracking-tight">transformerus</span>
          </Link>
        </div>

        <div className="p-3 flex-1 overflow-y-auto">
          {user ? (
            <div className={`p-3 rounded-2xl mb-4 border ${lowBalance ? 'bg-amber-50 border-amber-200' : 'bg-neutral-50 border-neutral-200'}`}>
              <div className="flex items-center gap-2 text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
                <Coins className={`w-3.5 h-3.5 ${lowBalance ? 'text-amber-600' : 'text-amber-500'}`} />
                Credits
              </div>
              <div className="mt-1 font-display text-2xl font-bold text-neutral-900">
                {balance === null ? '—' : balance}
              </div>
              <Button
                onClick={() => setCreditsOpen(true)}
                size="sm"
                data-testid="builder-topup-btn"
                className={`mt-2 w-full h-8 rounded-lg text-xs ${
                  lowBalance ? 'bg-amber-600 hover:bg-amber-700' : 'bg-neutral-900 hover:bg-neutral-800'
                } text-white`}
              >
                <Zap className="w-3 h-3 mr-1" /> Top up
              </Button>
            </div>
          ) : (
            <div className="p-3 rounded-2xl mb-4 bg-gradient-to-br from-emerald-50 to-cyan-50 border border-emerald-200">
              <div className="text-[11px] font-semibold text-emerald-800 uppercase tracking-wider">Free preview</div>
              <p className="mt-1 text-xs text-neutral-700">Sign in for 10 free credits and to save your builds.</p>
              <Button onClick={login} size="sm" data-testid="builder-signin-btn" className="mt-2 w-full h-8 rounded-lg text-xs bg-neutral-900 text-white">
                <LogIn className="w-3 h-3 mr-1" /> Sign in
              </Button>
            </div>
          )}

          <p className="px-2 py-1 text-[11px] font-semibold text-neutral-500 tracking-wider uppercase">Quick Starts</p>
          {SAMPLE_PROMPTS.map((p) => (
            <button
              key={p.short}
              onClick={() => sendMessage(p.full)}
              data-testid={`builder-quick-${p.short.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
              className="w-full text-left text-xs px-2.5 py-2 rounded-lg hover:bg-neutral-100 text-neutral-700 transition-colors line-clamp-2"
            >
              {p.short}
            </button>
          ))}
        </div>

        <div className="p-3 border-t border-neutral-200">
          <Link to="/">
            <Button variant="ghost" className="w-full justify-start text-xs gap-2 h-8">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to home
            </Button>
          </Link>
        </div>
      </aside>

      {/* Chat column */}
      <main className="flex flex-col w-full lg:w-[420px] lg:flex-shrink-0 lg:border-r border-neutral-200 bg-white">
        <header className="h-14 border-b border-neutral-200 flex items-center px-4 gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-neutral-100" aria-label="Open menu">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-white">
              <div className="p-5 border-b border-neutral-200">
                <Link to="/" className="flex items-center gap-2.5">
                  <img src="/logo.png" alt="Transformerus" className="w-8 h-8 object-contain" />
                  <span className="tfu-logo text-[18px] tracking-tight">transformerus</span>
                </Link>
              </div>
              <div className="p-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 70px)' }}>
                <p className="px-2 py-2 text-[11px] font-semibold text-neutral-500 tracking-wider uppercase">Quick Starts</p>
                {SAMPLE_PROMPTS.map((p) => (
                  <button key={p.short} onClick={() => sendMessage(p.full)}
                    className="w-full text-left text-sm px-3 py-2.5 rounded-lg hover:bg-neutral-100 text-neutral-700 transition-colors line-clamp-2">
                    {p.short}
                  </button>
                ))}
                <Link to="/" className="mt-5 flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 px-3 py-2">
                  <ArrowLeft className="w-4 h-4" /> Back to home
                </Link>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-cyan-500 flex-shrink-0" />
            <h1 className="font-display font-semibold text-neutral-900 truncate text-sm">
              {projectName || 'Build with Transformerus'}
            </h1>
            {user && (
              isPro ? (
                <span
                  data-testid="builder-pro-badge"
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold uppercase tracking-wider shadow-sm"
                >
                  <Sparkles className="w-2.5 h-2.5" /> Pro
                </span>
              ) : (
                <Link
                  to="/pricing"
                  data-testid="builder-upgrade-pill"
                  className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 hover:bg-violet-50 text-neutral-600 hover:text-violet-700 text-[10px] font-semibold uppercase tracking-wider transition-colors"
                  title="Upgrade for premium code-gen with Claude"
                >
                  ✨ Upgrade
                </Link>
              )
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {user && (
              <button
                onClick={() => setCreditsOpen(true)}
                data-testid="builder-credit-pill"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-neutral-50 border border-neutral-200 text-xs font-medium text-neutral-700 hover:bg-neutral-100"
              >
                <Coins className="w-3.5 h-3.5 text-amber-500" />
                <span>{balance === null ? '—' : balance}</span>
              </button>
            )}
          </div>
        </header>

        <ScrollArea className="flex-1" ref={scrollRef}>
          <div className="px-4 py-5 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-cyan-200 to-sky-300 mb-4">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-display text-2xl font-bold text-neutral-900">What should we build?</h2>
                <p className="mt-2 text-sm text-neutral-600 px-4">Describe your app and I'll generate working React code with a live preview.</p>
                <div className="mt-5 flex flex-wrap gap-1.5 justify-center px-2">
                  {SAMPLE_PROMPTS.slice(0, 4).map((p) => (
                    <button
                      key={p.short}
                      onClick={() => sendMessage(p.full)}
                      className="text-[11px] px-2.5 py-1.5 rounded-full bg-white border border-neutral-200 hover:border-neutral-400 text-neutral-700 transition-colors"
                    >
                      {p.short}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-300 to-sky-400 flex-shrink-0 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-neutral-900 text-white'
                      : m.outOfCredits
                      ? 'bg-amber-50 border border-amber-200 text-amber-900'
                      : 'bg-neutral-50 border border-neutral-200 text-neutral-800'
                  }`}
                >
                  {m.content}
                  {m.outOfCredits && (
                    <Button
                      onClick={() => setCreditsOpen(true)}
                      className="mt-2 h-8 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs"
                    >
                      <Coins className="w-3.5 h-3.5 mr-1.5" /> Buy credits
                    </Button>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-2.5">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-cyan-300 to-sky-400 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-neutral-50 border border-neutral-200 rounded-2xl px-3.5 py-2.5 inline-flex items-center gap-2 text-neutral-500 text-sm">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Building your app…
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Composer */}
        <ProUpsellBanner user={user} isPro={isPro} buildsToday={buildsToday} />
        <div className="border-t border-neutral-200 bg-white p-3">
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="relative bg-neutral-50 border border-neutral-200 rounded-2xl focus-within:border-neutral-400 transition-colors"
          >
            <Textarea
              data-testid="builder-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={hasProject ? 'Iterate — e.g. "make the buttons rounded"' : 'Describe what to build…'}
              rows={2}
              className="resize-none border-0 bg-transparent shadow-none focus-visible:ring-0 text-sm pr-12 py-3"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="builder-send-btn"
              className="absolute right-2 bottom-2 w-9 h-9 p-0 rounded-xl bg-neutral-900 hover:bg-neutral-800 disabled:opacity-40"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
            </Button>
          </form>
          <p className="text-[10px] text-neutral-400 text-center mt-2">
            {user
              ? `Each build uses 1 credit. ${balance !== null ? `Balance: ${balance}.` : ''}`
              : 'Free preview — sign in for 10 starter credits.'}
          </p>
        </div>
      </main>

      {/* Preview / Code column */}
      <section className="hidden lg:flex flex-col flex-1 bg-white">
        <header className="h-14 border-b border-neutral-200 flex items-center px-4 gap-3">
          <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 bg-neutral-50">
            <button
              onClick={() => setActiveView('preview')}
              data-testid="builder-view-preview"
              className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                activeView === 'preview' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-900'
              }`}
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
            <button
              onClick={() => setActiveView('code')}
              data-testid="builder-view-code"
              className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                activeView === 'code' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-900'
              }`}
            >
              <Code2 className="w-3.5 h-3.5" /> Code
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={async () => {
                  try {
                    const r = await axios.post(
                      `${API}/admin/cleanup-errors`,
                      {},
                      { withCredentials: true },
                    );
                    toast.success(`Cleaned ${r.data?.deleted || 0} stale error messages`);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Cleanup failed');
                  }
                }}
                data-testid="builder-admin-cleanup-btn"
                title="Admin: wipe stale error bubbles from every user's chat history"
                className="h-8 px-2.5 inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 text-[11px] font-semibold transition-colors"
              >
                🧹 Clean errors
              </button>
            )}
            <button
              onClick={() => setShareOpen(true)}
              disabled={!hasProject}
              data-testid="builder-share-btn"
              title="Share this build"
              className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40 text-neutral-700 transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
            <Button
              onClick={() => setDeployCfOpen(true)}
              disabled={!hasProject}
              size="sm"
              data-testid="builder-deploy-cf-btn"
              className="h-8 text-xs rounded-lg bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white disabled:opacity-50 border-0"
            >
              <Rocket className="w-3.5 h-3.5 mr-1.5" /> Deploy live
            </Button>
            <Button
              onClick={() => setDeployOpen(true)}
              disabled={!hasProject}
              size="sm"
              data-testid="builder-deploy-btn"
              variant="outline"
              className="h-8 text-xs rounded-lg disabled:opacity-50"
            >
              <Github className="w-3.5 h-3.5 mr-1.5" /> GitHub
            </Button>
            <Button
              onClick={() => downloadProject(projectName, projectFiles)}
              disabled={!hasProject}
              size="sm"
              variant="outline"
              data-testid="builder-download-btn"
              className="h-8 text-xs rounded-lg"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
          </div>
        </header>
        <div className="flex-1 min-h-0 relative">
          {missingDep && (
            <div
              data-testid="builder-dep-error-banner"
              className="absolute top-3 right-3 z-20 max-w-sm flex items-start gap-2 px-3 py-2 rounded-xl border border-amber-300 bg-amber-50 shadow-lg"
            >
              <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] leading-snug text-amber-900">
                  Missing dependency: <code className="font-mono">{missingDep}</code>
                </p>
                <Button
                  onClick={regenerateWithoutMissingDep}
                  disabled={loading}
                  size="sm"
                  data-testid="builder-regen-without-dep-btn"
                  className="mt-1.5 h-7 px-2 rounded-md text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                >
                  <Loader2 className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : 'hidden'}`} />
                  🔁 Regenerate without it
                </Button>
              </div>
              <button
                onClick={() => setMissingDep(null)}
                aria-label="Dismiss"
                className="text-amber-500 hover:text-amber-800 p-0.5 rounded transition-colors flex-shrink-0"
              >
                ✕
              </button>
            </div>
          )}
          <SandpackBuilder
            files={projectFiles}
            entry={projectEntry}
            activeView={activeView}
            onDependencyError={handleDependencyError}
          />
        </div>
      </section>

      <BuyCreditsModal open={creditsOpen} onOpenChange={setCreditsOpen} />
      <DeployGitHubModal
        open={deployOpen}
        onOpenChange={setDeployOpen}
        sessionId={sessionId}
        projectName={projectName}
        hasProject={hasProject}
        isAuthed={!!user}
        onLogin={login}
      />
      <DeployCloudflareModal
        open={deployCfOpen}
        onOpenChange={setDeployCfOpen}
        sessionId={sessionId}
        projectName={projectName}
        hasProject={hasProject}
        isAuthed={!!user}
        onLogin={login}
      />
      <ShareBuildModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        sessionId={sessionId}
        projectName={projectName}
        hasProject={hasProject}
      />
    </div>
  );
}
